/**
 * aiJobStore — 백그라운드 AI 생성 Job 추적 (Zustand)
 *
 * 페이지 이동 시에도 진행 중인 AI 생성 작업을 추적한다.
 * sessionStorage에 persist되어 새로고침에도 유지되고,
 * 탭 종료 시 자동 정리된다.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

type JobStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'PARTIAL' | 'FAILED' | 'CANCELLED'

const TERMINAL_STATUSES = new Set<JobStatus>(['COMPLETED', 'PARTIAL', 'FAILED', 'CANCELLED'])
const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000 // 24시간

export interface BackgroundJob {
  jobId: string
  startedAt: number
  status: JobStatus
  totalImages: number
  completedImages: number
  generatedUrls: string[]
  error: string | null
  /** 사용자가 결과를 확인했는지 (toast 표시 후 AI Studio 재방문) */
  acknowledged: boolean
}

interface AIJobState {
  jobs: Record<string, BackgroundJob>
  registerJob: (jobId: string, totalImages: number) => void
  updateJob: (jobId: string, update: Partial<BackgroundJob>) => void
  acknowledgeJob: (jobId: string) => void
  removeJob: (jobId: string) => void
  /** 24시간 지난 terminal job 정리 */
  pruneStaleJobs: () => void
  getActiveJobs: () => BackgroundJob[]
  getCompletedUnacknowledged: () => BackgroundJob[]
  hasActiveJob: () => boolean
}

export const useAIJobStore = create<AIJobState>()(
  persist(
    (set, get) => ({
      jobs: {},

      registerJob: (jobId, totalImages) => {
        if (!jobId) return
        set((s) => ({
          jobs: {
            ...s.jobs,
            [jobId]: {
              jobId,
              startedAt: Date.now(),
              status: 'PENDING',
              totalImages,
              completedImages: 0,
              generatedUrls: [],
              error: null,
              acknowledged: false,
            },
          },
        }))
      },

      updateJob: (jobId, update) =>
        set((s) => {
          const existing = s.jobs[jobId]
          if (!existing) return s
          return { jobs: { ...s.jobs, [jobId]: { ...existing, ...update } } }
        }),

      acknowledgeJob: (jobId) =>
        set((s) => {
          const existing = s.jobs[jobId]
          if (!existing) return s
          return { jobs: { ...s.jobs, [jobId]: { ...existing, acknowledged: true } } }
        }),

      removeJob: (jobId) =>
        set((s) => {
          const { [jobId]: _, ...rest } = s.jobs
          return { jobs: rest }
        }),

      pruneStaleJobs: () =>
        set((s) => {
          const now = Date.now()
          const pruned: Record<string, BackgroundJob> = {}
          for (const [id, job] of Object.entries(s.jobs)) {
            const age = now - job.startedAt
            if (age > STALE_THRESHOLD_MS) {
              // terminal 상태면 제거, active 상태면 FAILED로 전환
              if (!TERMINAL_STATUSES.has(job.status)) {
                pruned[id] = { ...job, status: 'FAILED', error: '세션 만료' }
              }
              // terminal + stale → 제거 (pruned에 추가하지 않음)
            } else {
              pruned[id] = job
            }
          }
          return { jobs: pruned }
        }),

      getActiveJobs: () => {
        const { jobs } = get()
        return Object.values(jobs).filter((j) => !TERMINAL_STATUSES.has(j.status))
      },

      getCompletedUnacknowledged: () => {
        const { jobs } = get()
        return Object.values(jobs).filter(
          (j) => !j.acknowledged && (j.status === 'COMPLETED' || j.status === 'PARTIAL')
        )
      },

      hasActiveJob: () => {
        const { jobs } = get()
        return Object.values(jobs).some((j) => !TERMINAL_STATUSES.has(j.status))
      },
    }),
    {
      name: 'cugumi-ai-jobs',
      storage: {
        getItem: (name) => {
          const value = sessionStorage.getItem(name)
          return value ? JSON.parse(value) : null
        },
        setItem: (name, value) => sessionStorage.setItem(name, JSON.stringify(value)),
        removeItem: (name) => sessionStorage.removeItem(name),
      },
    }
  )
)
